<template>
    <div>
        <PageDescriptionEditor page-title="Partners"
            default-description="Building meaningful partnerships to amplify diverse voices and expand our reach." />

        <div class="mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">Partners Management</h2>
        </div>

        <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-4">{{ editingPartner ? 'Edit' : 'Add' }} Partner</h3>
            <form @submit.prevent="savePartner" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Name</label>
                        <input v-model="formData.name" type="text" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div>
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Display Order</label>
                        <input v-model.number="formData.display_order" type="number" min="0"
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Partner Image</label>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <input ref="fileInput" type="file" accept="image/*,.heic,.heif"
                                    @change="handleFileSelect" class="hidden" />
                                <button type="button" @click="() => fileInput?.click()"
                                    class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                    Upload Image
                                </button>
                                <input v-model="formData.image_url" type="url" placeholder="Or enter image URL"
                                    class="flex-1 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                            </div>
                            <div v-if="imagePreview || (formData.image_url && !uploading)" class="mt-2 space-y-3">
                                <div class="flex gap-3 items-start">
                                    <div :style="getImageContainerStyle()"
                                        class="border border-creme-300 overflow-hidden bg-white flex items-center justify-center">
                                        <img :src="imagePreview || formData.image_url" alt="Preview"
                                            :style="getImageStyle()" class="w-full h-full object-cover" />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <button type="button" @click="loadImageForCropping"
                                            :disabled="showCropModal || loadingCropImage"
                                            :class="['px-4 py-2 rounded-md transition-colors font-body text-[1.125rem] leading-[1.75]', (showCropModal || loadingCropImage) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-accent text-white hover:bg-green-700']">
                                            {{ loadingCropImage ? 'Loading...' : 'Crop Image' }}
                                        </button>
                                        <button type="button" @click="clearImage"
                                            class="px-4 py-2 text-[1.125rem] text-red-600 hover:text-red-700 font-body leading-[1.75]">
                                            Remove Image
                                        </button>
                                    </div>
                                </div>
                                <div v-if="formData.image_crop_dimensions && (formData.image_crop_dimensions.width > 0 || formData.image_crop_dimensions.height > 0)"
                                    class="space-y-2">
                                    <div class="font-body text-[1.125rem] leading-[1.75] text-gray-600">
                                        Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                                            Math.round(formData.image_crop_dimensions.height) }}px
                                    </div>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Width
                                                (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.width" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-[1.125rem] bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body leading-[1.75]" />
                                        </div>
                                        <div>
                                            <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Height
                                                (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.height" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-[1.125rem] bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body leading-[1.75]" />
                                        </div>
                                    </div>
                                    <div class="p-3 bg-creme-100 rounded-md border border-creme-300">
                                        <p class="text-xs font-header font-bold text-primary mb-2">True-to-Scale
                                            Preview:</p>
                                        <div class="flex items-center justify-center">
                                            <div :style="getTrueToScalePreviewStyle()"
                                                class="border-2 border-gray-400 bg-white overflow-hidden shadow-sm">
                                                <img :src="imagePreview || formData.image_url" alt="True scale preview"
                                                    class="w-full h-full object-cover" />
                                            </div>
                                        </div>
                                        <p class="text-xs text-gray-500 font-body text-body mt-2 text-center">This is
                                            how the
                                            image will appear at actual size</p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="uploading" class="text-sm text-gray-600 font-marketing">
                                Uploading...
                            </div>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <label class="block text-body font-header mb-1">Description</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="descriptionEditor" :content="formData.description"
                                @update:content="formData.description = $event"
                                placeholder="Write the partner description here..." />
                        </div>
                    </div>
                    <div class="col-span-2">
                        <label class="block text-body font-header mb-1">Capacity</label>
                        <textarea v-model="formData.capacity" rows="3"
                            placeholder="Describe the partner's capacity/services..."
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body"></textarea>
                    </div>
                    <div class="col-span-2">
                        <label class="block text-body font-header mb-1">Links</label>
                        <div v-for="(link, index) in formData.links" :key="index" class="flex gap-2 mb-2">
                            <input v-model="link.label" type="text" placeholder="Link label"
                                class="flex-1 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                            <input v-model="link.url" type="url" placeholder="URL"
                                class="flex-2 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                            <button type="button" @click="removeLink(index)"
                                class="px-3 py-2 text-[1.125rem] text-red-600 hover:text-red-700 font-header">Remove</button>
                        </div>
                        <button type="button" @click="addLink"
                            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-header text-[1.125rem]">
                            Add Link
                        </button>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button type="submit"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem]">
                        {{ editingPartner ? 'Update' : 'Create' }}
                    </button>
                    <button type="button" @click="cancelForm"
                        class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-header text-[1.125rem]">
                        Cancel
                    </button>
                </div>
            </form>
        </div>

        <div class="mb-4 flex justify-end">
            <button @click="openNewPartnerForm"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem]">
                Add New Partner
            </button>
        </div>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="w-full overflow-x-auto">
                <table class="w-full min-w-max">
                    <thead class="bg-green-700 text-white">
                        <tr>
                            <th class="px-4 py-3 text-left font-header">Order</th>
                            <th class="px-4 py-3 text-left font-header">Name</th>
                            <th class="px-4 py-3 text-left font-header">Image</th>
                            <th class="px-4 py-3 text-left font-header">Description</th>
                            <th class="px-4 py-3 text-left font-header">Capacity</th>
                            <th class="px-4 py-3 text-left font-header">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="partners.length === 0" class="border-b border-creme-200">
                            <td colspan="6" class="px-4 py-8 text-center text-gray-500 font-body text-body">
                                No partners found. Click "Add New Partner" to create one.
                            </td>
                        </tr>
                        <tr v-for="partner in partners" :key="partner.id"
                            class="border-b border-creme-200 hover:bg-creme-50">
                            <td class="px-4 py-3 font-header text-gray-900">{{ partner.display_order }}</td>
                            <td class="px-4 py-3 font-header text-gray-900">{{ partner.name }}</td>
                            <td class="px-4 py-3">
                                <img v-if="partner.image_url" :src="partner.image_url" :alt="partner.name"
                                    class="w-16 h-16 object-cover" />
                                <span v-else class="text-gray-400 font-body text-body">No image</span>
                            </td>
                            <td class="px-4 py-3 font-body text-body text-gray-900">
                                <span v-if="partner.description" class="line-clamp-2">{{
                                    partner.description.substring(0,
                                        100) }}{{ partner.description.length > 100 ? '...' : '' }}</span>
                                <span v-else class="text-gray-400 font-body text-body">No description</span>
                            </td>
                            <td class="px-4 py-3 font-body text-body text-gray-900">
                                <span v-if="partner.capacity" class="line-clamp-2">{{ partner.capacity.substring(0, 100)
                                }}{{ partner.capacity.length > 100 ? '...' : '' }}</span>
                                <span v-else class="text-gray-400 font-body text-body">No capacity</span>
                            </td>
                            <td class="px-4 py-3">
                                <button @click="editPartner(partner)"
                                    class="text-green-700 hover:text-green-800 mr-3 font-header text-[1.125rem]">Edit</button>
                                <button @click="deletePartner(partner.id)"
                                    class="text-red-600 hover:text-red-700 font-header text-[1.125rem]">Delete</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadPartners(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem]', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-header text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadPartners(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem]', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
            <div class="mb-4">
                <h3 class="font-header text-[1.5rem] font-bold text-primary">Crop Partner Image</h3>
            </div>
            <div v-if="loadingCropImage" class="mb-4 flex items-center justify-center" style="height: 700px;">
                <Loader size="large" />
            </div>
            <div v-else-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
                <Cropper :key="`cropper-${cropModalKey}-${cropImageSrc}`" ref="cropper" :src="cropImageSrc"
                    :default-boundaries="'fill'" :stencil-props="{
                        aspectRatio: null
                    }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
                <p class="text-gray-500 font-body text-body">No image selected</p>
            </div>

            <div v-if="cropResult && cropResult.coordinates"
                class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
                <h4 class="text-sm font-header font-bold text-primary mb-3">Preview</h4>
                <div class="flex items-center justify-center">
                    <div class="flex flex-col items-center">
                        <div :style="getPreviewStyle(cropResult.coordinates)"
                            class="bg-white border-2 border-gray-300 shadow-md mb-2 overflow-hidden flex items-center justify-center">
                            <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview"
                                class="w-full h-full object-cover" />
                        </div>
                        <p class="text-xs text-gray-500 font-body text-body mt-1">{{
                            Math.round(cropResult.coordinates.width) }} ×
                            {{ Math.round(cropResult.coordinates.height) }}px</p>
                    </div>
                </div>
            </div>

            <div class="flex gap-3 justify-end">
                <button @click="closeCropModal"
                    class="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-header font-bold text-[1.125rem]">
                    Cancel
                </button>
                <button @click="applyCrop"
                    class="px-6 py-2 bg-accent text-white rounded-md hover:bg-green-700 transition-colors font-header font-bold text-[1.125rem]">
                    Apply Crop
                </button>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { adminService } from '../../services/admin'
import { API_BASE_URL } from '../../config'
import { useScrollToTop } from '../../composables/useScrollToTop'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const { scrollToTop } = useScrollToTop()
import BaseModal from './BaseModal.vue'
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import RichTextEditor from '../common/RichTextEditor.vue'

const partners = ref([])
const showForm = ref(false)
const editingPartner = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const imagePreview = ref('')
const uploading = ref(false)
const fileInput = ref(null)

const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const imageNaturalSize = ref({ width: 0, height: 0 })
const loadingCropImage = ref(false)
const cropModalKey = ref(0)
const descriptionEditor = ref(null)

const formData = ref({
    name: '',
    image_url: '',
    description: convertLegacyQuillContent(''),
    capacity: '',
    links: [],
    display_order: 0,
    image_crop_dimensions: null
})

const loadPartners = async (page = 1) => {
    try {
        const response = await adminService.getPartners(page, limit.value)
        if (response.data && response.pagination) {
            partners.value = response.data || []
            pagination.value = response.pagination
            currentPage.value = page
        } else {
            const data = Array.isArray(response) ? response : []
            partners.value = data
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load partners:', error)
        partners.value = []
        pagination.value = null
    }
}

const editPartner = async (partner) => {
    editingPartner.value = partner
    formData.value = {
        name: partner.name,
        image_url: partner.image_url || '',
        description: convertLegacyQuillContent(partner.description || ''),
        capacity: partner.capacity || '',
        links: partner.links && partner.links.length > 0 ? [...partner.links] : [],
        display_order: partner.display_order || 0,
        image_crop_dimensions: partner.image_crop_dimensions || null
    }
    imagePreview.value = ''
    showForm.value = true
    await nextTick()
    if (descriptionEditor.value) {
        descriptionEditor.value.setHTML(formData.value.description || '')
    }
}

const openNewPartnerForm = async () => {
    editingPartner.value = null
    formData.value = {
        name: '',
        image_url: '',
        description: convertLegacyQuillContent(''),
        capacity: '',
        links: [],
        display_order: 0,
        image_crop_dimensions: null
    }
    imagePreview.value = ''
    uploading.value = false
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    showForm.value = true
    await nextTick()
    if (descriptionEditor.value) {
        descriptionEditor.value.setHTML(convertLegacyQuillContent(''))
    }
}

const cancelForm = async () => {
    showForm.value = false
    editingPartner.value = null
    formData.value = {
        name: '',
        image_url: '',
        description: '',
        capacity: '',
        links: [],
        display_order: 0,
        image_crop_dimensions: null
    }
    imagePreview.value = ''
    uploading.value = false
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    await nextTick()
    if (descriptionEditor.value) {
        descriptionEditor.value.setHTML(convertLegacyQuillContent(''))
    }
    scrollToTop()
}

const addLink = () => {
    formData.value.links.push({ label: '', url: '' })
}

const removeLink = (index) => {
    formData.value.links.splice(index, 1)
}

const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const isImage = file.type.startsWith('image/') ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif')
    if (!isImage) {
        alert('Please select an image file')
        return
    }

    if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB')
        return
    }

    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    try {
        const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
        const convertedFile = await convertHeicForUpload(file)
        selectedFile.value = convertedFile

        const blobUrl = await convertHeicForPreview(file)
        imagePreview.value = blobUrl
    } catch (error) {
        console.error('Failed to convert HEIC:', error)
        selectedFile.value = file
        const blobUrl = URL.createObjectURL(file)
        imagePreview.value = blobUrl
    }
}

const loadImageForCropping = async () => {
    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    const imageUrl = imagePreview.value || formData.value.image_url
    if (!imageUrl) {
        alert('Please upload an image or enter an image URL first')
        return
    }

    loadingCropImage.value = true
    cropResult.value = null
    previewImageUrl.value = ''
    imageNaturalSize.value = { width: 0, height: 0 }

    let blobUrl = imagePreview.value

    try {
        if (!blobUrl || !blobUrl.startsWith('blob:')) {
            let blob
            if (imageUrl.includes('storage.googleapis.com')) {
                const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
                const response = await fetch(proxyUrl, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            } else {
                const response = await fetch(imageUrl)
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            }

            blobUrl = URL.createObjectURL(blob)
            imagePreview.value = blobUrl
        }

        cropImageSrc.value = blobUrl
        selectedFile.value = null
        cropModalKey.value++
        showCropModal.value = true
        await nextTick()
    } catch (error) {
        console.error('Failed to load image for cropping:', error)
        alert('Failed to load image. Please check the image URL or try uploading the image again.')
    } finally {
        loadingCropImage.value = false
    }
}

const closeCropModal = () => {
    const fileToUpload = selectedFile.value

    if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc.value)
    }
    showCropModal.value = false
    cropResult.value = null
    cropImageSrc.value = ''
    previewImageUrl.value = ''
    imageNaturalSize.value = { width: 0, height: 0 }
    loadingCropImage.value = false
    cropper.value = null

    if (fileToUpload && !formData.value.image_url) {
        imagePreview.value = ''
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    } else if (!formData.value.image_url) {
        imagePreview.value = ''
    }

    selectedFile.value = null
}

const onCropperReady = () => {
    if (cropper.value) {
        const result = cropper.value.getResult()
        if (result?.imageSize) {
            imageNaturalSize.value = {
                width: result.imageSize.width,
                height: result.imageSize.height
            }
        }
    }
}

const onCropChange = ({ coordinates, canvas }) => {
    cropResult.value = { coordinates, canvas }
    if (canvas) {
        previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
    }
}

const applyCrop = async () => {
    if (!cropper.value || !cropResult.value) {
        alert('Please select a crop area by adjusting the crop box')
        return
    }

    try {
        const { coordinates, canvas } = cropResult.value

        if (!coordinates || !coordinates.width || !coordinates.height) {
            alert('Please select a crop area')
            return
        }

        formData.value.image_crop_dimensions = {
            width: coordinates.width,
            height: coordinates.height
        }

        if (canvas) {
            uploading.value = true

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert('Failed to create cropped image')
                    uploading.value = false
                    return
                }

                try {
                    const currentImageUrl = formData.value.image_url || imagePreview.value
                    let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
                    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
                    fileName = `${nameWithoutExt}.jpg`

                    const formDataUpload = new FormData()
                    formDataUpload.append('file', blob, fileName)

                    const uploadedUrl = await adminService.uploadImage(formDataUpload)
                    if (uploadedUrl) {
                        formData.value.image_url = uploadedUrl
                        imagePreview.value = uploadedUrl

                        if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
                            URL.revokeObjectURL(cropImageSrc.value)
                        }

                        uploading.value = false
                        showCropModal.value = false
                        selectedFile.value = null
                        cropResult.value = null
                    } else {
                        throw new Error('No URL returned from server')
                    }
                } catch (error) {
                    console.error('Failed to upload cropped image:', error)
                    const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
                    alert(errorMessage)
                    uploading.value = false
                }
            }, 'image/jpeg', 0.95)
        } else {
            showCropModal.value = false
        }
    } catch (error) {
        console.error('Error applying crop:', error)
        alert('Failed to apply crop. Please try again.')
    }
}

const getPreviewStyle = (coordinates) => {
    if (!coordinates || !coordinates.width || !coordinates.height) {
        return { width: '200px', height: '200px' }
    }

    let displayWidth = coordinates.width
    let displayHeight = coordinates.height

    const MAX_WIDTH = 300
    if (coordinates.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / coordinates.width
        displayWidth = MAX_WIDTH
        displayHeight = coordinates.height * scale
    }

    return {
        width: `${displayWidth}px`,
        height: `${displayHeight}px`
    }
}

const getImageStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxWidth = 200
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            objectFit: 'cover',
            objectPosition: 'center center'
        }
    }
    return {
        maxWidth: '200px',
        height: '192px',
        objectFit: 'cover',
        objectPosition: 'center center'
    }
}

const getImageContainerStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxWidth = 200
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    }
    return {
        width: '200px',
        height: '192px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}

const getTrueToScalePreviewStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxPreviewWidth = 400
        let displayWidth = width
        let displayHeight = height

        if (width > maxPreviewWidth) {
            const scale = maxPreviewWidth / width
            displayWidth = maxPreviewWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`
        }
    }
    return {
        width: '200px',
        height: '200px'
    }
}

const updatePreviewDimensions = () => {
    if (!formData.value.image_crop_dimensions) {
        formData.value.image_crop_dimensions = { width: 0, height: 0 }
    }
}

const Math = window.Math

const clearImage = () => {
    formData.value.image_url = ''
    formData.value.image_crop_dimensions = null
    imagePreview.value = ''
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const savePartner = async () => {
    try {
        // First, upload the blob image if there's a selected file that hasn't been uploaded yet
        if (selectedFile.value && imagePreview.value && imagePreview.value.startsWith('blob:')) {
            uploading.value = true
            try {
                const formDataUpload = new FormData()
                formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
                const uploadedUrl = await adminService.uploadImage(formDataUpload)
                formData.value.image_url = uploadedUrl
                imagePreview.value = uploadedUrl
                selectedFile.value = null
            } catch (error) {
                console.error('Failed to upload image:', error)
                alert('Failed to upload image. Please try again.')
                uploading.value = false
                return
            } finally {
                uploading.value = false
            }
        }

        const partnerData = {
            name: formData.value.name,
            image_url: formData.value.image_url || null,
            description: formData.value.description || null,
            capacity: formData.value.capacity || null,
            links: formData.value.links.filter(link => link.label && link.url),
            display_order: formData.value.display_order || 0,
            image_crop_dimensions: formData.value.image_crop_dimensions || null
        }

        if (partnerData.image_url && partnerData.image_crop_dimensions &&
            partnerData.image_crop_dimensions.width > 0 && partnerData.image_crop_dimensions.height > 0) {
            try {
                const { resizeImage } = await import('../../utils/imageResize.js')
                const resizedBlob = await resizeImage(
                    partnerData.image_url,
                    partnerData.image_crop_dimensions.width,
                    partnerData.image_crop_dimensions.height
                )
                const formDataUpload = new FormData()
                formDataUpload.append('file', resizedBlob, 'resized-image.jpg')
                const resizedUrl = await adminService.uploadImage(formDataUpload)
                partnerData.image_url = resizedUrl
            } catch (error) {
                console.error('Failed to resize image:', error)
            }
        }

        if (editingPartner.value) {
            await adminService.updatePartner(editingPartner.value.id, partnerData)
        } else {
            await adminService.createPartner(partnerData)
        }

        await loadPartners(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save partner:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Failed to save partner. Please try again.'
        alert(errorMessage)
    }
}

const deletePartner = async (id) => {
    if (!confirm('Are you sure you want to delete this partner?')) return

    try {
        await adminService.deletePartner(id)
        await loadPartners(currentPage.value)
    } catch (error) {
        console.error('Failed to delete partner:', error)
        alert('Failed to delete partner. Please try again.')
    }
}

onMounted(() => {
    loadPartners(1)
})
</script>

<style scoped>
:deep(.ql-editor) {
    min-height: 100px;
    font-family: 'Manrope', sans-serif;
    font-size: 18px;
    line-height: 1.75;
    color: #253439;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Manrope', sans-serif;
    direction: ltr !important;
    unicode-bidi: embed !important;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FAFAFA;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}
</style>
